import { IIOPensionInputs } from "app/calcs/customers/IO/interfaces"
import {
    ILocal701PensionInputs,
    Local701FormOfPayment,
} from "app/calcs/customers/local701/interfaces"
import {
    INEBFPensionInputs,
    NEBFFormOfPayment,
} from "app/calcs/customers/NEBF/interfaces"
import { ISharedPensionInputs } from "app/calcs/customers/shared/interfaces"
import {
    IMonthlyPensionPayments,
    SimplePensionFormOfPayment,
} from "app/calcs/pensions/interfaces"
import {
    ActionTypes,
    ILocal701OpAction,
    ILocal701OpState,
} from "app/redux/Customers/local701/reducer"
import {
    ageAt,
    IRetirementPerson,
    isSamePersonForSSA,
    isValidPerson,
    personHasRetired,
    Profile,
} from "app/redux/Profile/model"
import { UserActions } from "app/redux/User/actions"
import { theCensusInfo } from "app/services/models/PublicCensusInfo"
import {
    IPokeandPullRequest,
    IRequestedValues,
    IWorkbookValues,
    WorkbookServiceResponse,
} from "app/services/models/Workbook"
import { OperationStatus } from "app/services/OperationStatus"
import { genericFailure } from "app/services/WebServiceBase"
import { WorkbookService } from "app/services/WorkbookService"
import * as utils from "app/utils"
import { INosyParker, nullNosyParker } from "app/utils/nosyParker"
import { getWebServicesConfig } from "config"
import { config } from "dotenv"
import * as _ from "lodash"
import * as moment from "moment"

interface INEBFRunCalculationsRequest {
    member: IRetirementPerson
    spouse: IRetirementPerson
    vals: INEBFPensionInputs
    shared: ISharedPensionInputs
    when: moment.Moment
}

const log = utils.makeDebug(false)
const sheetDateFormat = "L"
const inputsSheetName = "Input"

const nebfSheetName = "Output NEBF"
const formOfPaymentMap = [
    { form: NEBFFormOfPayment.lifeOnly, range: "c4:i43" },
    {
        form: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
        range: "m4:s43",
    },
    {
        form: SimplePensionFormOfPayment.seventyFivePercentJointAndSurvivor,
        range: "w4:ac43",
    },
]
const nebfSheetRange = _.map(formOfPaymentMap, (fop) => fop.range)

const nebfCalculatorSheetName = "Calculator_NEBF"
const nebfCalculatorSheetRange = "b4:d32"

function makeNEBFPaymentExtractor(
    vals: INEBFPensionInputs,
    when: moment.Moment
) {
    return (wr: WorkbookServiceResponse): number => {
        if (_.isUndefined(vals.formOfPayment)) {
            return 0
        }
        if (!wr.Results) {
            return 0
        }
        const output = wr.Results[nebfSheetName]
        const tableKey = _.find(
            formOfPaymentMap,
            (q) => q.form === vals.formOfPayment
        )

        // find the table corresponding to the form of payment
        const table = output[tableKey.range]

        // find the row for the year we need
        let row = _.find(table, (r) => r[0] === when.year())
        if (!row) {
            row = _.first(table)
        }
        if (!row) {
            // tslint:disable-next-line:no-debugger
            debugger
            return 0
        }

        // some worksheets use 'financial' formatting which formats 0's as '-'
        if ("-" === row[6]) {
            row[6] = 0
        }
        return row[6] || 0
    }
}

type NEBFWorkbookDAOCalculator = (
    request: INEBFRunCalculationsRequest
) => Promise<WorkbookServiceResponse>

class NEBFWorkbookDAO {
    private service: WorkbookService = new WorkbookService(
        getWebServicesConfig().workbookEndpoint,
        "local701Workbook"
    )

    constructor(protected nosyParker: INosyParker = nullNosyParker) {}

    /**
     * Run the NEBF calculations workbook.
     *
     * @param request The request parameters
     *
     * @returns a WorkbookServiceResponse.
     */
    runCalculations(
        request: INEBFRunCalculationsRequest
    ): Promise<WorkbookServiceResponse> {
        log("NEBFWorkbookDAO using service: " + this.service)

        this.nosyParker.operationIsStarting()
        const startTime = moment.utc()

        return this.issueRequest(request)
            .then((wr: WorkbookServiceResponse) => {
                const elapsed = moment.utc().diff(startTime)

                log(
                    "It took " + elapsed + "ms for NEBFWorkbookDAO calculations"
                )

                if (wr.IsError || !wr.Results) {
                    // tslint:disable-next-line:no-debugger
                    //debugger
                    this.nosyParker.operationHasFailed(wr)
                } else {
                    this.nosyParker.operationHasCompleted(wr)
                }

                return wr
            })
            .catch((reason) => {
                this.nosyParker.operationHasFailed(reason)
                return Promise.reject(reason)
            })
    }

    issueRequest(
        request: INEBFRunCalculationsRequest
    ): Promise<WorkbookServiceResponse> {
        const spousePresent = isValidPerson(request.spouse)
        const spouseDOB = spousePresent
            ? request.spouse.dob.format(sheetDateFormat)
            : ""
        const spouseLifeExpectancy = spousePresent
            ? request.spouse.selectedAgeExpectancy
            : ""
        if (!request.member) {
            // tslint:disable-next-line:no-debugger
            debugger
        }

        if (_.isNumber(request.vals.currentYearsService)) {
            // tslint:disable-next-line:no-debugger
            debugger
        }

        const yos = utils.isNullOrUndefined(request.vals.currentYearsService)
            ? 0
            : request.vals.currentYearsService.asYears()

        const asOfDate = theCensusInfo.censusDate || moment()

        const inputs: IWorkbookValues = {
            [inputsSheetName]: {
                c26: asOfDate.format(sheetDateFormat),
                "c7:c11": [
                    [request.member.dob.format(sheetDateFormat)],
                    [spouseDOB],
                    [request.member.selectedAgeExpectancy],
                    [spouseLifeExpectancy],
                    [request.shared.hoursWorked],
                ],
                "c20:c21": [[request.vals.beginPaymentsAge.years()], [yos]],
            },
        }

        const outputs: IRequestedValues = {
            [nebfSheetName]: nebfSheetRange,
        }

        const req: IPokeandPullRequest = { inputs, outputs }
        return this.service.pokeAndPull(
            UserActions.getSharedVar("accessToken"),
            req
        )
    }
}

export {
    INEBFRunCalculationsRequest,
    NEBFWorkbookDAOCalculator,
    NEBFWorkbookDAO,
    makeNEBFPaymentExtractor,
    nebfCalculatorSheetName,
    nebfCalculatorSheetRange,
    nebfSheetName,
    nebfSheetRange,
}



// WEBPACK FOOTER //
// ./src/app/workbookDAOs/NEBFDAO/index.ts